import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product, CartItem, NailShape, NailLength, NailSizePreset, NailSizeMeasurements, AddOnItem } from '../types';

interface CartContextType {
  cartItems: CartItem[];
  selectedAddOns: AddOnItem[];
  isCartOpen: boolean;
  promoCode: string;
  discountPercentage: number;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addToCart: (
    product: Product,
    shape: NailShape,
    length: NailLength,
    sizePreset: NailSizePreset,
    customMeasurements?: NailSizeMeasurements,
    quantity?: number
  ) => void;
  updateQuantity: (cartId: string, quantity: number) => void;
  removeFromCart: (cartId: string) => void;
  toggleAddOn: (addOn: AddOnItem) => void;
  applyPromoCode: (code: string) => { success: boolean; message: string };
  removePromoCode: () => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
  discountAmount: number;
  shippingFee: number;
  taxAmount: number;
  finalTotal: number;
  freeShippingProgress: number; // 0 to 100
  freeShippingNeeded: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const FREE_SHIPPING_THRESHOLD = 50.0;
const STANDARD_SHIPPING_FEE = 4.95;
const ESTIMATED_TAX_RATE = 0.06; // 6%

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('nailed_it_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [selectedAddOns, setSelectedAddOns] = useState<AddOnItem[]>(() => {
    try {
      const saved = localStorage.getItem('nailed_it_addons');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState(0);

  // Sync to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('nailed_it_cart', JSON.stringify(cartItems));
    } catch (e) {
      console.error(e);
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      localStorage.setItem('nailed_it_addons', JSON.stringify(selectedAddOns));
    } catch (e) {
      console.error(e);
    }
  }, [selectedAddOns]);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen((prev) => !prev);

  const addToCart = (
    product: Product,
    shape: NailShape,
    length: NailLength,
    sizePreset: NailSizePreset,
    customMeasurements?: NailSizeMeasurements,
    quantity = 1
  ) => {
    // Generate a unique ID based on product, shape, length, size
    const customKey = customMeasurements
      ? `${customMeasurements.thumb}-${customMeasurements.index}-${customMeasurements.middle}-${customMeasurements.ring}-${customMeasurements.pinky}`
      : '';
    const cartId = `${product.id}_${shape}_${length}_${sizePreset}_${customKey}`;

    setCartItems((prev) => {
      const existing = prev.find((item) => item.cartId === cartId);
      if (existing) {
        return prev.map((item) =>
          item.cartId === cartId ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        const newItem: CartItem = {
          cartId,
          product,
          shape,
          length,
          sizePreset,
          customMeasurements,
          quantity,
          unitPrice: product.price,
          addedAt: Date.now(),
        };
        return [...prev, newItem];
      }
    });

    setIsCartOpen(true);
  };

  const updateQuantity = (cartId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(cartId);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) => (item.cartId === cartId ? { ...item, quantity } : item))
    );
  };

  const removeFromCart = (cartId: string) => {
    setCartItems((prev) => prev.filter((item) => item.cartId !== cartId));
  };

  const toggleAddOn = (addOn: AddOnItem) => {
    setSelectedAddOns((prev) => {
      const exists = prev.some((a) => a.id === addOn.id);
      if (exists) {
        return prev.filter((a) => a.id !== addOn.id);
      } else {
        return [...prev, addOn];
      }
    });
  };

  const applyPromoCode = (code: string) => {
    const cleanCode = code.trim().toUpperCase();
    if (cleanCode === 'KYLEE10' || cleanCode === 'SPARKLE10') {
      setPromoCode(cleanCode);
      setDiscountPercentage(0.10); // 10%
      return { success: true, message: '🎉 10% discount applied to your order!' };
    } else if (cleanCode === 'GYMNAST20') {
      setPromoCode(cleanCode);
      setDiscountPercentage(0.20); // 20%
      return { success: true, message: '🤸‍♀️ 20% Gymnast Club discount applied!' };
    } else if (cleanCode === 'FREESHIP') {
      setPromoCode(cleanCode);
      setDiscountPercentage(0);
      return { success: true, message: '📦 Free shipping unlocked!' };
    } else {
      return { success: false, message: 'Invalid promo code. Try "KYLEE10" for 10% off!' };
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setDiscountPercentage(0);
  };

  const clearCart = () => {
    setCartItems([]);
    setSelectedAddOns([]);
    setPromoCode('');
    setDiscountPercentage(0);
    localStorage.removeItem('nailed_it_cart');
    localStorage.removeItem('nailed_it_addons');
  };

  // Calculations
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  
  const productsSubtotal = cartItems.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  const addOnsSubtotal = selectedAddOns.reduce((sum, item) => sum + item.price, 0);
  const subtotal = productsSubtotal + addOnsSubtotal;

  const discountAmount = subtotal * discountPercentage;
  const taxableSubtotal = Math.max(0, subtotal - discountAmount);

  const isFreeShipping =
    subtotal >= FREE_SHIPPING_THRESHOLD || promoCode === 'FREESHIP' || subtotal === 0;
  const shippingFee = isFreeShipping ? 0 : STANDARD_SHIPPING_FEE;
  
  const taxAmount = taxableSubtotal * ESTIMATED_TAX_RATE;
  const finalTotal = taxableSubtotal + shippingFee + taxAmount;

  const freeShippingProgress = Math.min(100, Math.round((subtotal / FREE_SHIPPING_THRESHOLD) * 100));
  const freeShippingNeeded = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        selectedAddOns,
        isCartOpen,
        promoCode,
        discountPercentage,
        openCart,
        closeCart,
        toggleCart,
        addToCart,
        updateQuantity,
        removeFromCart,
        toggleAddOn,
        applyPromoCode,
        removePromoCode,
        clearCart,
        itemCount,
        subtotal,
        discountAmount,
        shippingFee,
        taxAmount,
        finalTotal,
        freeShippingProgress,
        freeShippingNeeded,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
